import { iconChevronSmall270, iconChevronSmall90, iconGlobe, iconPerson, iconSettingsAlt, } from '@tesla/design-system-icons';
import { getDisplayName } from '@tesla/intl-display-names';
import { parseLocale } from '@tesla/parse-locale';
import cx from 'classnames';
import { decode } from 'html-entities';
import React, { useEffect, useRef, useState } from 'react';
import { Icon, LocaleSelector, LocaleSelectorTrigger, Modal, ModalContent, NavItem, SiteHeader, SiteLogo, NavItemGroup, useId, } from '../index.js';
import { useMediaQuery } from '../hooks/use-media-query.js';
import { useApprovedLocales } from '../hooks/use-approved-locales.js';
import { useMenuData } from './use-menu-data.js';
/**
 * GlobalMenu (deprecated)
 * @deprecated GlobalMenu was originally an opinionated template for the tesla.com menu
 * and has been superseded by the Tesla OS Navigation pattern for internal apps and tesla.com's Mega Menu pattern for external apps
 * - Tesla OS Navigation docs: https://design.tesla.com/docs/components/tesla-os-navigation
 * - MegaMenu (TCL) Repo: https://github.tesla.com/digital-experience/fnd-tcl
 * - MegaMenu Live demo: https://tcl.tesla.com/?p=organisms-mega-menu
 * - MegaMenuProd: https://www.tesla.com/
 */
export const GlobalMenu = ({ customComponents, data: externalData, disableLocaleSelectorAutoOpen, endpoint, fadeIn = true, locale = 'en-US', locales, onLocaleSelect, onMount, overrideLocaleDelimiter, overrideSheetScrim = 'white', variant, ...rest }) => {
    var _a, _b, _c, _d, _e, _f;
    /**
     * Data
     */
    const { data, headerRef } = useMenuData({ endpoint, externalData, onMount });
    // Current locale parsing
    const { country: countryCode, lang: languageCode } = parseLocale(locale);
    /**
     * Menu State & Actions
     */
    const [open, setOpen] = useState(false);
    const [history, setHistory] = useState([]);
    const [currentMenus, setCurrentMenus] = useState([]);
    // Switch to a different set of menu keys
    // - Save current state for the back link
    const goToNewMenu = (menuKeys) => {
        // If this is the first opening of a menu, open the modal and ignore history
        if (!open) {
            setOpen(true);
            setCurrentMenus([...menuKeys]);
        }
        // Otherwise, navigate a layer deeper into the menu structure
        else {
            const prevMenus = [...history];
            prevMenus.push(currentMenus);
            setHistory(prevMenus);
            setCurrentMenus([...menuKeys]);
        }
    };
    const dialogRef = useRef(null);
    useEffect(() => {
        var _a, _b;
        if (dialogRef.current) {
            dialogRef.current.style.scrollBehavior = 'auto';
            (_b = (_a = dialogRef.current).scrollTo) === null || _b === void 0 ? void 0 : _b.call(_a, 0, 0);
            dialogRef.current.style.scrollBehavior = '';
        }
    }, [currentMenus]);
    // Use the back link
    const goBack = () => {
        const lastState = history.pop() || [];
        setCurrentMenus(lastState);
        setHistory(history);
    };
    /**
     * Monitor user locale for mismatches
     */
    const { approveLocale, isApprovedLocale } = useApprovedLocales(locale);
    // Open locale selector if the user's languages doesn't match the page's
    // unless the user has previously dismissed it (or externally disabled)
    useEffect(() => {
        // Under no circumstances auto-pop in China, even if authoring sends the localeSelector key
        const isChina = countryCode === 'CN';
        if (!isApprovedLocale && !disableLocaleSelectorAutoOpen && (data === null || data === void 0 ? void 0 : data.localeSelector) && !isChina) {
            setOpen(true);
            setCurrentMenus(['localeSelector']);
        }
    }, [
        isApprovedLocale,
        data.loaded,
        countryCode,
        disableLocaleSelectorAutoOpen,
        data === null || data === void 0 ? void 0 : data.localeSelector,
    ]);
    /**
     * Monitor viewport size for responsiveness
     */
    const { isDesktopUp } = useMediaQuery();
    // Unknown how many icons we'll have to support long-term
    const iconMap = {
        globe: React.createElement(Icon, { data: iconGlobe }),
        person: React.createElement(Icon, { data: iconPerson }),
        settings: React.createElement(Icon, { data: iconSettingsAlt }),
        // cart
        // search
    };
    const rightItems = isDesktopUp
        ? data === null || data === void 0 ? void 0 : data.primaryNavigationRightItems
        : data === null || data === void 0 ? void 0 : data.primaryMobileNavigationRightItems;
    const uId = useId();
    const resolveIcon = (icon) => {
        // TODO: Revisit this once v7 is out the door
        if (typeof icon === 'string') {
            return iconMap[icon] || null;
        }
        return icon;
    };
    /**
     * Nav Items
     * - Based on menu data,
     * - generate each menu item & hook it up to state
     */
    const GlobalNavItemGroup = ({ align, animated: externalAnimated, items, layout, ...rest }) => (React.createElement(NavItemGroup, { align: align, layout: layout, ...rest }, items.map((item, index) => {
        var _a, _b, _c;
        // Process the raw data
        let title = decode(item.title);
        const key = `${uId}_${item.href || index}`;
        let startIcon = resolveIcon(item === null || item === void 0 ? void 0 : item.startIcon);
        let endIcon = resolveIcon(item === null || item === void 0 ? void 0 : item.endIcon);
        let highlighted = item.highlighted;
        let animated = (_a = item.animated) !== null && _a !== void 0 ? _a : externalAnimated;
        let readOnly = item.readOnly;
        // Allow a user-provided component to override any type of nav item
        if (item.special &&
            customComponents &&
            Object.keys(customComponents).includes(item.special)) {
            return React.createElement(React.Fragment, { key: key }, customComponents[item.special]);
        }
        // If rendering the locale selector trigger, go full custom
        if (item.special === 'localeSelectorTrigger' && (data === null || data === void 0 ? void 0 : data.localeSelector)) {
            // Generate country and language names if not provided by authoring
            const country = ((_b = data === null || data === void 0 ? void 0 : data.translatableText) === null || _b === void 0 ? void 0 : _b.localeSelectorTriggerCountry) ||
                getDisplayName(countryCode, locale, { type: 'region' });
            const language = ((_c = data === null || data === void 0 ? void 0 : data.translatableText) === null || _c === void 0 ? void 0 : _c.localeSelectorTriggerLanguage) ||
                getDisplayName(languageCode, locale, { type: 'language' });
            return (React.createElement(LocaleSelectorTrigger, { country: country, key: key, language: language, onClick: () => {
                    goToNewMenu(item.action || []);
                }, onKeyPress: (e) => {
                    if (e.code === 'Space' || e.code === 'Enter') {
                        goToNewMenu(item.action || []);
                    }
                } }));
        }
        // If it's a special kind of nav item, just make a few changes
        if (item.special === 'localeSelectorNavItem') {
            title = countryCode;
            startIcon = React.createElement(Icon, { data: iconGlobe });
            endIcon = '';
            // If in CN, make sure any locale selectors' actions are disabled,
            // regardless of what authoring sends us (per @vbarclay)
            if (countryCode === 'CN') {
                item.action = undefined;
                item.href = undefined;
                readOnly = true;
            }
        }
        // Make sure the Menu item is always highlighted
        if (item.special === 'menuNavItem') {
            animated = false;
            highlighted = true;
            endIcon = '';
        }
        // Make sure that items that don't go anywhere don't appear clickable
        if (!item.href && !item.action) {
            readOnly = true;
        }
        // If item points to a non-existant menu, assume there's an authoring error and hide the item
        if (item.action) {
            // Check if the menu keys this link points to have content in them
            const actionExists = item.action.some((action) => !!data[action]);
            if (!actionExists)
                return null;
        }
        // Otherwise, render default nav item
        return (React.createElement(NavItem, { ...item, animated: animated, endIcon: 
            // Provide a default arrow only inside the modal/vertical items
            (endIcon !== null && endIcon !== void 0 ? endIcon : (layout === 'vertical' && item.action)) ? (React.createElement(Icon, { data: iconChevronSmall90 })) : undefined, highlighted: highlighted, href: item.href, key: key, onClick: item.action
                ? () => {
                    goToNewMenu(item.action || []);
                }
                : undefined, readOnly: readOnly, startIcon: startIcon, ...rest }, title));
    })));
    return (React.createElement("div", { className: "tds-global-menu", id: "tds-global-menu", ref: data.loaded ? headerRef : undefined },
        React.createElement(SiteHeader, { animated: true, fadeIn: fadeIn, variant: variant, ...rest },
            React.createElement(SiteLogo, { appTitle: (_a = data === null || data === void 0 ? void 0 : data.translatableText) === null || _a === void 0 ? void 0 : _a.appTitle, appTitleHref: (_b = data === null || data === void 0 ? void 0 : data.logo) === null || _b === void 0 ? void 0 : _b.appTitleHref, href: (_c = data === null || data === void 0 ? void 0 : data.logo) === null || _c === void 0 ? void 0 : _c.href, label: ((_d = data === null || data === void 0 ? void 0 : data.translatableText) === null || _d === void 0 ? void 0 : _d.logoLabel) || 'Tesla', skipLink: ((_e = data === null || data === void 0 ? void 0 : data.translatableText) === null || _e === void 0 ? void 0 : _e.skipToMainContentText) || 'Skip to main content' }),
            isDesktopUp && (data === null || data === void 0 ? void 0 : data.primaryNavigationItems) && (React.createElement(GlobalNavItemGroup, { align: "center", animated: true, className: "tds--product-name", items: data === null || data === void 0 ? void 0 : data.primaryNavigationItems })),
            rightItems && (React.createElement(GlobalNavItemGroup, { align: "end", animated: isDesktopUp, className: "tds--product-name", items: rightItems }))),
        React.createElement(Modal, { className: cx('tds-site-header-modal', 'tds-modal--sheet-small', {
                [`tds-scrim--${overrideSheetScrim}`]: overrideSheetScrim,
            }), dialogRef: dialogRef, header: !!history.length && (React.createElement(NavItem, { className: "tds-nav-back", onClick: goBack, startIcon: React.createElement(Icon, { data: getComputedStyle(document.body).direction === 'rtl'
                        ? iconChevronSmall90
                        : iconChevronSmall270 }) }, (_f = data === null || data === void 0 ? void 0 : data.translatableText) === null || _f === void 0 ? void 0 : _f.backLinkText)), onClose: () => {
                setOpen(false);
                // If a user closes the menu, assume they're fine with the current locale
                if (!isApprovedLocale) {
                    approveLocale(locale);
                }
            }, onCloseAnimationFinish: () => {
                setHistory([]);
                setCurrentMenus([]);
            }, open: open, variant: "sheet-right" },
            React.createElement(ModalContent, null, currentMenus.map((menuKey) => {
                if (menuKey === 'localeSelector' && (data === null || data === void 0 ? void 0 : data.localeSelector)) {
                    return (React.createElement(LocaleSelector, { className: "tds-locale-selector--narrow", currentLocale: {
                            countryCode,
                            languageCode,
                            localeCode: locale,
                        }, delimiter: overrideLocaleDelimiter, key: menuKey, locales: locales, onLocaleSelect: (e, selectedLocale) => {
                            setOpen(false);
                            setHistory([]);
                            setCurrentMenus([]);
                            // If a user selects a specific locale, approve it
                            approveLocale(selectedLocale);
                            if (onLocaleSelect) {
                                onLocaleSelect(e, selectedLocale);
                            }
                        }, superRegions: data === null || data === void 0 ? void 0 : data.localeSelector }));
                }
                if (data[menuKey]) {
                    return (React.createElement(GlobalNavItemGroup
                    // @ts-expect-error TS doesn't like strings as keys sometimes (TODO: investigate)
                    , { 
                        // @ts-expect-error TS doesn't like strings as keys sometimes (TODO: investigate)
                        items: data[menuKey], key: menuKey, layout: "vertical" }));
                }
                return null;
            })))));
};
